import { endOfDay, startOfDay } from "date-fns";

/**
 * Normalize a picked date range to inclusive day boundaries.
 *
 * Calendar pickers hand back the selected day at 00:00:00. The stats views all
 * filter sessions with `sessionDate > endDate`, so a raw end date of
 * `22 juin 00:00:00` drops every session that actually happened during 22 juin.
 * Expanding start -> startOfDay and end -> endOfDay keeps the whole first and
 * last selected day inside the range (matching the dates shown to the user).
 */
export const toInclusiveDayRange = (
  start: Date | null,
  end: Date | null,
): { startDate: Date | null; endDate: Date | null } => ({
  startDate: start ? startOfDay(start) : null,
  endDate: end ? endOfDay(end) : null,
});

/**
 * Day-boundary-aware "is this session inside the selected range?" check.
 *
 * Every stats view filters sessions against the picked range. A calendar end
 * date arrives at 00:00:00, so a naive `sessionDate > endDate` drops every
 * session that happened during the last selected day (the customer-reported
 * bug). Normalizing the bounds here (start -> startOfDay, end -> endOfDay) makes
 * the range inclusive of the whole first and last day, regardless of the
 * time-of-day carried by the range. Idempotent with toInclusiveDayRange, so it
 * stays correct even though the producer (DateRangePicker) already normalizes —
 * the consumers no longer depend on that invariant holding upstream.
 *
 * A null bound means "unbounded" on that side (the "Depuis toujours" preset).
 */
export const isSessionInDateRange = (
  sessionDate: Date,
  dateRange: { startDate: Date | null; endDate: Date | null },
): boolean => {
  if (dateRange.startDate && sessionDate < startOfDay(dateRange.startDate)) return false;
  if (dateRange.endDate && sessionDate > endOfDay(dateRange.endDate)) return false;
  return true;
};

/**
 * Get session date from created_at_ts (Unix timestamp) or fallback to created_at (ISO string)
 * @param session - Session object with created_at_ts and/or created_at
 * @returns Date object
 */
export const getSessionDate = (session: {
  created_at_ts?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
}): Date => {
  // Primary: use created_at_ts (Unix timestamp in seconds)
  if (session.created_at_ts && session.created_at_ts > 0) {
    return new Date(session.created_at_ts * 1000);
  }

  // Fallback: use created_at (Strapi auto-generated ISO string)
  if (session.created_at) {
    return new Date(session.created_at);
  }

  // Fallback: use createdAt (alternative naming convention)
  if (session.createdAt) {
    return new Date(session.createdAt);
  }

  // Last resort: return current date (should not happen)
  return new Date();
};

/**
 * Get session timestamp in milliseconds
 * @param session - Session object with created_at_ts and/or created_at
 * @returns Timestamp in milliseconds
 */
export const getSessionTimestamp = (session: {
  created_at_ts?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
}): number => {
  return getSessionDate(session).getTime();
};
